/**
 * CE R4: Sprout-style "next open slot" queue resolver.
 *
 * Users don't pick dates/times. A tenant defines weekly posting windows
 * (e.g. Mon 9am, Wed 3pm, Fri noon) and generated posts drop into the next
 * free window, collisions pushed forward. Pure + deterministic (caller passes
 * `now`) so it is trivially unit-testable.
 */

/** A recurring weekly posting window in the tenant's local time. */
export interface PostingWindow {
  /** Day of week, 0 = Sunday … 6 = Saturday. */
  day: number;
  /** Hour 0–23. */
  hour: number;
  /** Minute 0–59 (default 0). */
  minute?: number;
}

/** A sensible default cadence when a tenant hasn't configured one: Mon/Wed/Fri. */
export const DEFAULT_POSTING_WINDOWS: PostingWindow[] = [
  { day: 1, hour: 9, minute: 0 }, // Monday 9:00
  { day: 3, hour: 15, minute: 0 }, // Wednesday 15:00
  { day: 5, hour: 12, minute: 0 }, // Friday 12:00
];

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * Convert `get_best_time` top_slots (`{dow: 'Tuesday', hour: 9}`) into
 * PostingWindow[] for `resolveNextOpenSlots`. The RPC emits full English day
 * names in the user's local timezone; unknown day strings are dropped and
 * duplicate (day, hour) pairs deduped. Returns [] when nothing converts —
 * callers must fall back to their existing default windows (honesty rule:
 * a gated/empty best-time result never silently becomes a personalized slot).
 */
export function bestSlotsToPostingWindows(
  slots: Array<{ dow: string; hour: number }>,
): PostingWindow[] {
  const seen = new Set<string>();
  const windows: PostingWindow[] = [];
  for (const slot of slots) {
    const day = DAY_NAME_TO_NUMBER[slot.dow?.toLowerCase?.() ?? ''];
    if (day === undefined || !Number.isInteger(slot.hour) || slot.hour < 0 || slot.hour > 23) {
      continue;
    }
    const key = `${day}-${slot.hour}`;
    if (seen.has(key)) continue;
    seen.add(key);
    windows.push({ day, hour: slot.hour, minute: 0 });
  }
  return windows;
}

function nextOccurrence(from: Date, window: PostingWindow): Date {
  const d = new Date(from.getTime());
  d.setSeconds(0, 0);
  const minute = window.minute ?? 0;
  // Advance day-by-day (max 7) until we hit the window's weekday at/after `from`.
  for (let i = 0; i < 8; i++) {
    if (d.getDay() === window.day) {
      const candidate = new Date(d.getTime());
      candidate.setHours(window.hour, minute, 0, 0);
      if (candidate.getTime() >= from.getTime()) return candidate;
    }
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
  }
  // Unreachable for a valid weekday, but return a safe far-future fallback.
  const fallback = new Date(from.getTime());
  fallback.setDate(fallback.getDate() + 7);
  fallback.setHours(window.hour, minute, 0, 0);
  return fallback;
}

/**
 * Resolve the next `count` open posting slots at/after `now`, skipping any slot
 * already taken (collisions pushed forward) and never double-booking within
 * this batch.
 *
 * @throws if `windows` is empty.
 */
export function resolveNextOpenSlots(
  windows: PostingWindow[],
  taken: Date[],
  count: number,
  now: Date,
  opts: { maxHorizonSlots?: number } = {},
): Date[] {
  if (windows.length === 0) {
    throw new Error('resolveNextOpenSlots requires at least one posting window');
  }
  if (count <= 0) return [];

  const takenMs = new Set(taken.map((d) => Math.floor(d.getTime() / 60_000)));
  const chosen: Date[] = [];
  const maxIterations = opts.maxHorizonSlots ?? Math.max(count * windows.length * 8, 64);

  let cursor = new Date(now.getTime());
  for (let iter = 0; iter < maxIterations && chosen.length < count; iter++) {
    // Find the soonest window occurrence at/after the cursor.
    let next: Date | null = null;
    for (const w of windows) {
      const occ = nextOccurrence(cursor, w);
      if (!next || occ.getTime() < next.getTime()) next = occ;
    }
    if (!next) break;

    const key = Math.floor(next.getTime() / 60_000);
    if (!takenMs.has(key)) {
      chosen.push(new Date(next.getTime()));
      takenMs.add(key); // don't double-book this slot within the batch
    }
    // Advance the cursor one minute past this occurrence to find the next.
    cursor = new Date(next.getTime() + 60_000);
  }

  return chosen;
}
